import TeamSourceBlock from '~/components/site/blocks/TeamSourceBlock.vue'
import JobsListBlock from '~/components/site/blocks/JobsListBlock.vue'
import BookingWidgetBlock from '~/components/site/blocks/BookingWidgetBlock.vue'
import NewsFeedBlock from '~/components/site/blocks/NewsFeedBlock.vue'
import HtmlEmbedBlock from '~/components/site/blocks/HtmlEmbedBlock.vue'
import ServicesListBlock from '~/components/site/blocks/ServicesListBlock.vue'

import TeamSourceSettings from '~/components/site/settings/TeamSourceSettings.vue'
import JobsListSettings from '~/components/site/settings/JobsListSettings.vue'
import BookingWidgetSettings from '~/components/site/settings/BookingWidgetSettings.vue'
import NewsFeedSettings from '~/components/site/settings/NewsFeedSettings.vue'
import HtmlEmbedSettings from '~/components/site/settings/HtmlEmbedSettings.vue'
import ServicesListSettings from '~/components/site/settings/ServicesListSettings.vue'

/**
 * Registers this app's domain-specific site blocks into the shared registry
 * (from the site-builder layer) at app init. Runs on both server and client
 * so SSR page renders can resolve block components.
 *
 * The generic blocks (hero, heading, rich_text, image, cta_banner, …) are
 * registered by the layer's own plugin (plugins/site-blocks-generic.ts); only
 * the domain blocks below live in this app.
 *
 * `meta.order` slots these into the layer's curated palette sequence — team
 * and jobs sit after `image` (40), the rest after `logo_strip` (130).
 */
export default defineNuxtPlugin(() => {
  // Re-registers the layer's generic `team` under the same key so this app's
  // 'bookable_staff' mode survives. App plugins run after layer plugins, so
  // this overwrites the layer's entry — and the backend mirrors the same
  // precedence by overriding only the `source` rule. Order matches the layer's
  // (85) so the palette position does not shift between the two products.
  registerSiteBlock('team', {
    component: TeamSourceBlock,
    settings: TeamSourceSettings,
    meta: { label: 'Team', icon: 'i-lucide-users', order: 85 },
    defaultData: { source: 'manual', headline: '', members: [] }
  })

  registerSiteBlock('jobs_list', {
    component: JobsListBlock,
    settings: JobsListSettings,
    meta: { label: 'Jobs list', icon: 'i-lucide-briefcase', order: 60 },
    defaultData: { jobs: [] }
  })

  // Directly after the layer's `pricing` (order 95, static plan cards) and
  // before `cards_grid` (100): both blocks answer "what does this cost", so
  // they belong next to each other in the palette — one for a few fixed
  // tiers, this one for a real catalogue.
  registerSiteBlock('services_list', {
    component: ServicesListBlock,
    settings: ServicesListSettings,
    meta: { label: 'Price list', icon: 'i-lucide-receipt-text', order: 96 },
    defaultData: {
      title: '',
      subtitle: '',
      layout: 'table',
      category: '',
      show_notes: true,
      show_surcharges: false
    }
  })

  registerSiteBlock('booking_widget', {
    component: BookingWidgetBlock,
    settings: BookingWidgetSettings,
    meta: { label: 'Booking widget', icon: 'i-lucide-calendar-check', order: 140 },
    defaultData: { title: 'Book a consultation' }
  })

  registerSiteBlock('news_feed', {
    component: NewsFeedBlock,
    settings: NewsFeedSettings,
    meta: { label: 'Latest news', icon: 'i-lucide-newspaper', order: 150 },
    defaultData: { title: 'Latest news', source: 'latest', count: 3 }
  })

  registerSiteBlock('html_embed', {
    component: HtmlEmbedBlock,
    settings: HtmlEmbedSettings,
    meta: { label: 'HTML embed', icon: 'i-lucide-code-2', order: 160 },
    defaultData: { file_url: '', height: 600 }
  })
})
